/**
 * One status vocabulary for every job type (scrape, batch, crawl, map, parse, search, monitor checks).
 *
 *   import { getStatus, normalizeStatus, STATUS } from "@/lib/status";
 *   getStatus("in_progress")   -> { key: "running", label: "Running", variant: "info", icon: ... }
 *   normalizeStatus("success") -> "completed"
 *   <StatusBadge status={log.status} />                      (components/ui/Badge.tsx)
 *
 * Six statuses only. Raw API values ("success", "in_progress", "pending", "error", "canceled", ...) are mapped
 * onto them, so a cancelled scrape never falls through to "Failed" and "In_progress" never leaks into the UI.
 * Unknown values fall back to "queued" style with the raw value humanized as the label.
 */
import type { BadgeVariant } from "@/components/ui/Badge";

export type StatusKey = "completed" | "running" | "queued" | "failed" | "cancelled" | "partial";

export interface StatusInfo {
  key: StatusKey;
  label: string;
  variant: BadgeVariant;
  icon: string;
  /** True while the job can still change, so pages know to keep polling. */
  active: boolean;
}

export const STATUS: Record<StatusKey, StatusInfo> = {
  completed: { key: "completed", label: "Completed", variant: "success", icon: "mdi:check-circle", active: false },
  running: { key: "running", label: "Running", variant: "info", icon: "mdi:progress-clock", active: true },
  queued: { key: "queued", label: "Queued", variant: "default", icon: "mdi:clock-outline", active: true },
  failed: { key: "failed", label: "Failed", variant: "error", icon: "mdi:alert-circle", active: false },
  cancelled: { key: "cancelled", label: "Cancelled", variant: "neutral", icon: "mdi:cancel", active: false },
  partial: { key: "partial", label: "Partial", variant: "warning", icon: "mdi:alert", active: false },
};

const ALIASES: Record<string, StatusKey> = {
  completed: "completed",
  complete: "completed",
  success: "completed",
  succeeded: "completed",
  successful: "completed",
  done: "completed",
  finished: "completed",
  ok: "completed",

  running: "running",
  in_progress: "running",
  inprogress: "running",
  processing: "running",
  active: "running",
  started: "running",
  crawling: "running",
  parsing: "running",
  scraping: "running",

  queued: "queued",
  pending: "queued",
  waiting: "queued",
  scheduled: "queued",
  created: "queued",
  new: "queued",

  failed: "failed",
  failure: "failed",
  error: "failed",
  errored: "failed",
  timeout: "failed",
  timed_out: "failed",

  cancelled: "cancelled",
  canceled: "cancelled",
  cancelling: "cancelled",
  aborted: "cancelled",
  stopped: "cancelled",

  partial: "partial",
  partial_success: "partial",
  partially_completed: "partial",
  completed_with_errors: "partial",
};

export function normalizeStatus(raw: string | null | undefined): StatusKey | null {
  if (!raw) return null;
  const k = String(raw).trim().toLowerCase().replace(/[\s-]+/g, "_");
  return ALIASES[k] ?? null;
}

/** Status info for any raw value. Unknown values keep a humanized label ("Rate_limited" -> "Rate limited"). */
export function getStatus(raw: string | null | undefined): StatusInfo {
  const key = normalizeStatus(raw);
  if (key) return STATUS[key];
  const label = raw ? humanize(String(raw)) : "Unknown";
  return { ...STATUS.queued, label };
}

export function humanize(value: string): string {
  const s = value.replace(/[_-]+/g, " ").replace(/([a-z])([A-Z])/g, "$1 $2").trim().toLowerCase();
  return s.charAt(0).toUpperCase() + s.slice(1);
}
